import type { Row } from "@rocicorp/zero";
import { keyframes, styled } from "goober";
import { useMemo, useRef } from "react";
import { type Color, colors } from "../../shared/colors";
import { useMouseTracking } from "../hooks/useMouseTracking";
import { userAvatarPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";
import { userDisplayName } from "../lib/userDisplayName";
import { Tooltip } from "./Tooltip";

type Props = {
  user: Row["user"];
  size?: number;
  isPresent?: boolean;
  isMuted?: boolean;
  tooltip?: boolean;
  // A bare avatar (the default) is a plain circle with no backdrop-coloured
  // pad/ring — correct for standalone avatars and see-through (cutout) stacks,
  // and it never mismatches the surface behind it. Pass `bare={false}` to paint
  // a backdrop-coloured separation pad, for overlapping stacks that separate
  // items by painting rather than by carving a cutout.
  bare?: boolean;
};

const Container = styled("div")`
  position: relative;
  border-radius: 50%;
  display: block;
  overflow: hidden;
  line-height: 0;
`;

const InnerShadow = styled("div")`
  position: absolute;
  border-radius: 50%;
  box-shadow: inset 0 0 0px 0.5px var(--border-alpha);
`;

const Image = styled("img")`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;

const FaceBackground = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
`;

const blink = keyframes`
  0%, 90%, 100% { transform: scaleY(1); }
  95% { transform: scaleY(0.05); }
`;

const FaceGroup = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const EyeRow = styled("div")`
  display: flex;
  gap: 3px;
  align-items: center;
  animation: ${blink} 4s ease-in-out infinite;
`;

const defaultSize = 24;

const ringColors = colors.map((c) => `var(--${c}-600)`);

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return hash >>> 0;
}

function getRingColor(seed: string): string {
  const index = hashString(seed) % ringColors.length;
  return ringColors[index]!;
}

function getAvatarColor(seed: string): string {
  const index = hashString(seed) % colors.length;
  return colors[index]!;
}

export function getUserColor(userId: string): Color {
  const index = hashString(userId) % colors.length;
  return colors[index]!;
}

function getMonogram(name: string | null | undefined): string {
  if (!name) return "?";
  return name.trim()[0] ?? "?";
}

// All face internals are designed at this reference size, then scaled
const refSize = 24;

function Eyes({ color }: { color: string }) {
  const eyeSize = 3;
  return (
    <>
      <svg width={eyeSize} height={eyeSize} viewBox="0 0 10 10">
        <circle cx="5" cy="5" r="5" fill={color} />
      </svg>
      <svg width={eyeSize} height={eyeSize} viewBox="0 0 10 10">
        <circle cx="5" cy="5" r="5" fill={color} />
      </svg>
    </>
  );
}

function Face({
  fgColor,
  monogram,
  animDelay,
  size,
}: {
  fgColor: string;
  monogram: string;
  animDelay: string;
  size: number;
}) {
  const faceRef = useRef<HTMLDivElement>(null);
  const maxOffset = size * 0.06;
  useMouseTracking(faceRef, maxOffset);

  return (
    <div style={{ transform: `scale(${size / refSize}) translateY(4%)` }}>
      <FaceGroup ref={faceRef}>
        <EyeRow style={{ animationDelay: animDelay }}>
          <Eyes color={fgColor} />
        </EyeRow>
        <span
          style={{
            fontSize: 8,
            fontWeight: 700,
            lineHeight: 1,
            color: fgColor,
            textTransform: "uppercase",
            letterSpacing: "-0.01em",
          }}
        >
          {monogram}
        </span>
      </FaceGroup>
    </div>
  );
}

export function Avatar({
  user,
  size = defaultSize,
  isPresent,
  isMuted,
  tooltip = true,
  bare = true,
}: Props) {
  const externalApiUrl = apiUrl();
  const ringColor = isPresent ? getRingColor(user.id) : undefined;
  const inset = 2;
  const pad = bare ? 0 : inset;
  const imageSrc = user.customAvatarKey
    ? `${externalApiUrl}${userAvatarPath(user.customAvatarKey)}`
    : (user.image ?? null);
  const hasImage = !!imageSrc;
  const tooltipContent = userDisplayName(user);

  const avatarColor = useMemo(
    () => (hasImage ? null : getAvatarColor(user.id)),
    [hasImage, user.id]
  );
  // Stagger blink animation per user so they don't all blink in sync
  const animDelay = useMemo(() => {
    if (hasImage) return undefined;
    const delay = -(hashString(user.id) % 4000) / 1000;
    return `${delay}s`;
  }, [hasImage, user.id]);

  const fgColor = avatarColor ? `var(--${avatarColor}-1000)` : undefined;

  const body = (
    <Container
      style={{
        width: size,
        height: size,
        boxSizing: bare ? "border-box" : "content-box",
        background: bare
          ? "transparent"
          : "var(--cell-bg, var(--surface-primary))",
        padding: pad,
        margin: -pad,
        boxShadow: ringColor
          ? bare
            ? `inset 0 0 0 1.5px ${ringColor}`
            : `inset 0 0 0 1px ${ringColor}, inset 0 0 0 2px var(--cell-bg, var(--surface-primary))`
          : bare
            ? "none"
            : `inset 0 0 0 1px var(--cell-bg, var(--surface-primary))`,
      }}
    >
      {hasImage ? (
        <Image
          src={imageSrc!}
          alt="Account avatar"
          referrerPolicy="no-referrer"
          style={{
            ...(isMuted && {
              filter: "grayscale(1)",
              opacity: 0.5,
            }),
          }}
        />
      ) : (
        <FaceBackground
          style={{
            background: `var(--${avatarColor}-600)`,
            ...(isMuted && {
              filter: "grayscale(1)",
              opacity: 0.5,
            }),
          }}
        >
          <Face
            fgColor={fgColor!}
            monogram={getMonogram(userDisplayName(user))}
            animDelay={animDelay!}
            size={size}
          />
        </FaceBackground>
      )}
      <InnerShadow style={{ top: pad, left: pad, right: pad, bottom: pad }} />
    </Container>
  );

  if (tooltip && tooltipContent) {
    return <Tooltip content={tooltipContent}>{body}</Tooltip>;
  }
  return body;
}

export function PlaceholderAvatar({ size = defaultSize }: { size?: number }) {
  const seed = "placeholder";
  const avatarColor = useMemo(() => getAvatarColor(seed), [seed]);
  const animDelay = useMemo(() => {
    const delay = -(hashString(seed) % 4000) / 1000;
    return `${delay}s`;
  }, [seed]);
  const fgColor = `var(--${avatarColor}-1000)`;

  return (
    <Container
      style={{
        width: size,
        height: size,
        boxSizing: "border-box",
      }}
    >
      <FaceBackground style={{ background: `var(--${avatarColor}-600)` }}>
        <Face
          fgColor={fgColor}
          monogram="?"
          animDelay={animDelay}
          size={size}
        />
      </FaceBackground>
      <InnerShadow style={{ top: 0, left: 0, right: 0, bottom: 0 }} />
    </Container>
  );
}
